import Dexie from 'dexie';
import bigInt from 'big-integer';
import Config from './config';
import Prediction from './prediction';
import SgfUtil from './sgf/sgfUtil';

class PredictionStore {

  static MIN_VISITS_TO_ADJUST_PP = 5;

  constructor(name) {
    this.name = name;
    this.map = new Map();
    this.lastFlushTime = Date.now();
    this.maxMapSize = Math.min(5000, Config.getInstance().nodesPerMove);
    this.doShrink = false;
    this.initializeDB();
  }

  initializeDB() {
    this.db = new Dexie(this.name);
    this.db.version(1).stores({ predictions: 'key,date' });
  }

  static getKey(history) {
    if (!history || !history.length) {
      return bigInt.zero.toString();
    }
    const boardModel = history[history.length - 1];
    if (boardModel.lastMove) {
      const index = SgfUtil.vertexToIndex(boardModel.lastMove,
        boardModel.boardSize);
      return boardModel.hash.add(index).toString();
    }
    return boardModel.hash.toString();
  }

  async get(key) {
    let item;
    if (this.map.has(key)) {
      item = this.map.get(key);
      if (item.p && item.p.length <= 362 && item.v && !isNaN(item.v)) {
        return this.createPrediction(item);
      }
    }
    item = await this.db.predictions.get(key);
    if (!item || !item.p || item.p.length > 362 || isNaN(item.v)) {
      return new Promise(resolve => {
        resolve(undefined);
      });
    }
    return this.createPrediction(item);
  }

  createPrediction(item) {
    const prediction = new Prediction();
    prediction.key = item.key;
    prediction.date = item.date;
    prediction.priorProbability = item.p;
    prediction.valueOutput = item.v;
    return prediction;
  }

  createDbItem(node) {
    const visited = node.edges.filter(edge => edge.visitCount >= PredictionStore.MIN_VISITS_TO_ADJUST_PP);
    if (visited && visited.length > 1) {
      const exponent = 4;
      const totalActionValue = visited.reduce((accumulator, edge) =>
        accumulator + edge.meanActionValue + 1, 0);
      const totalActionValuePower = visited.reduce((accumulator, edge) =>
        accumulator + Math.pow(edge.meanActionValue + 1, exponent), 0);  
      const totalProbability = visited.reduce((accumulator, edge) =>
        accumulator + edge.priorProbability, 0);
      // let totalDiff = 0;
      // console.log("totalActionValue: " + totalActionValue);
      // console.log("totalProbability: " + totalProbability);
      const average = totalActionValue / visited.length;
      if (average < 1.8 && average > 0.2) { // if win rate is too high, bad moves are included.
        for (const edge of visited) {
          // console.log("Edge: ", edge);
          // const oldValue = edge.priorProbability;
          const newValue = totalProbability * Math.pow(edge.meanActionValue + 1, exponent) / totalActionValuePower;
          edge.priorProbability = newValue;
          node.priorProbability[edge.nextMove] = newValue;
          // totalDiff += newValue - oldValue;
          // if (Math.abs(newValue - oldValue) > 0.05) {
          //   const vertex = SgfUtil.indexToGtpVertex(edge.nextMove, 19);
          //   console.log(`probability(${edge.nextMove} ${vertex}): ${oldValue.toFixed(3)} -> ${newValue.toFixed(3)}`, node);
          // }
        }
      }
      // console.log(`total diff: ${totalDiff}`);
    }
    return {
      key: node.key,
      date: Date.now(),
      p: node.priorProbability,
      v: node.meanEvaluation
    }
  }

  async put(node) {
    // to avoid bug
    if (node.edges.length !== 362) {
      console.warn("Illegal node.edges.length: " + node.edges.length);
      return;
    }
    const item = this.createDbItem(node);
    this.map.set(node.key, item);
    if (this.needFlush()) {
      this.flush();
    }
  }

  flush() {
    if (!this.map.size) {
      return;
    }
    const items = Array.from(this.map.values());
    this.map.clear();
    this.db.predictions.bulkPut(items)
      .catch((reason) => console.error(reason));
    this.lastFlushTime = Date.now();
    // console.log(`bulkPut ${items.length} items`);
  }

  needFlush() {
    const size = this.map.size;
    if (this.lastFlushTime + 30000 < Date.now() && size > 100) {
      return true;
    } else if (this.lastFlushTime + 300000 < Date.now()) {
      return true;
    } else if (size > this.maxMapSize) {
      return true;
    }
    return false;
  }

  async delete(key) {
    try {
      await this.db.predictions.delete(key);
    } catch (reason) {
      console.error(reason);
    }
  }

  count() {
    return this.db.predictions.count();
  }

  async shrink() {
    const lastShrinkTime = Number(Config.getInstance().get("lastShrink", 0));
    if (lastShrinkTime + 30 * 60 * 1000 > Date.now()) {
      return;
    }
    Config.getInstance().set("lastShrink", Date.now());
    this.doShrink = true;
    let size = await this.count()
    const maxDbSize = Config.getInstance().maxDbSize;
    while (size > maxDbSize) {
      const limit = Math.min(1000, Math.abs(maxDbSize - size));
      if (limit < 1) {
        break;
      } else if (!this.doShrink) {
        console.log("Stop shrinking");
        break;
      }
      const deleteCount =
        await this.db.predictions
          .orderBy("date")
          .limit(limit)
          .delete();
      console.log("Deleted " + deleteCount + " items from DB");
      size -= deleteCount;
    }
    console.log("Number of items: " + size);
    return size;
  }

  stopShrink() {
    this.doShrink = false;
  }

  clear() {
    this.db.predictions.clear();
  }

  deleteDB() {
    Dexie.delete(this.name)
      .then(() => {
        this.initializeDB();
        console.log("Database successfully deleted");
      }).catch((err) => {
        console.error("Could not delete database");
      });
  }
}

export default PredictionStore;